import React, { useEffect, useState } from 'react';
import { Typography, Container, Grid, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Switch } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Header from '../Header/Header';
import { Roles, AdminAccountItem } from '@beable/roster-backend-client-v1';
import AddAdminModal from './Modals/AddAdminModal';
import EditAdminPasswordModal from './Modals/EditAdminPasswordModal';
import DeleteAdminModal from './Modals/DeleteAdminModal';
import adminManagementService from '../../services/AdminManagementService';
import { Admin } from '../../interfaces/Admin';
import authService from '../../services/AuthService'
import BasePanel from '../Common/Layout/BasePanel';
import { BasePanelBox } from '../Common/Layout/BasePanelBox';
import BaseSheet from '../Common/Layout/BaseSheet';
import { StyledPanelHeader } from '../Common/Layout/PanelHeader';
import ReactivateAdminModal from './Modals/ReactivateAdminModal';
import { getStyles } from './styles';


const emptySelectedAdmin: Admin = {
    username: "",
    password: "",
    id: '1',
    role: Roles.beableadmin,
    site: 1
};

const initialAdminsValue: AdminAccountItem[] = [];

export default function UserManagement() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [selectedAdmin, setSelectedAdmin] = useState(emptySelectedAdmin);

    const [openModal, setOpenModal] = useState<undefined | 'add' | 'editpw' | 'delete' | 'reactivate'>(undefined)
    const [activeOnly, setActiveOnly] = useState(true);
    const [shouldFetchAdmins, setShouldFetchAdmins] = useState(true);
    const [adminListItems, setAdminListItems] = useState(initialAdminsValue);
    const [profile] = useState(authService.getUser());

    const closeAddAdminModal = (success?: boolean) => {
        setOpenModal(undefined);
        setShouldFetchAdmins(!!success);
    }

    const closePWModal = () => setOpenModal(undefined);

    const closeDeleteModal = (deleted?: boolean) => {
        setOpenModal(undefined);
        setShouldFetchAdmins(!!deleted);
        if (deleted) setSelectedAdmin(emptySelectedAdmin);
    }

    const closeReactivateAdminModal = (reactivated?: boolean) => {
        setOpenModal(undefined);
        setShouldFetchAdmins(!!reactivated);
        if (reactivated) setSelectedAdmin(emptySelectedAdmin);
    }

    const handleTableRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (e) {
            e.persist();
        }
        e.stopPropagation();
        const selectedRow = e.currentTarget;
        const selAdmin = adminListItems.find(administrator => administrator.id === selectedRow.id);

        if (selAdmin && selAdmin.id !== selectedAdmin.id) {
            setSelectedAdmin(selAdmin);
        } else {
            setSelectedAdmin(emptySelectedAdmin);
        }
    }

    const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setActiveOnly(event.target.checked);
    }

    const adminList = () => {
        if (activeOnly) {
            return adminListItems.filter((a) => !!a.active);
        }

        return adminListItems;
    }

    async function getAllAdmins() {
        const admins = await adminManagementService.getAllAdmins();
        if (admins) {
            setAdminListItems(admins);
        }
        setShouldFetchAdmins(false);
    }

    useEffect(() => {
        if (shouldFetchAdmins) {
            getAllAdmins();
        }
    }, [shouldFetchAdmins]);

    return (
        <>
            <Header />
            <Grid container>
                <BasePanel>
                    <BasePanelBox>
                        <StyledPanelHeader header="Admin User Management" />
                        <Grid container size={12}>
                            <Typography variant="h5" sx={{ marginLeft: '16px' }}>General Options</Typography>
                            <Container sx={styles.dfb}>
                                <Button sx={styles.verticalBtn} variant="contained" color="primary" onClick={() => setOpenModal('add')}>Add new admin</Button>
                            </Container>
                            {selectedAdmin?.username ? (
                                <>
                                    <Typography variant="h5" sx={{ marginLeft: '16px' }}>Options {selectedAdmin.username !== '' ? `for ${selectedAdmin.username}` : ''}</Typography>
                                    <Container sx={styles.dfb}>
                                        {selectedAdmin.active
                                            ? (<>
                                                <Button sx={styles.verticalBtn} variant="contained" color="secondary" onClick={() => setOpenModal('editpw')}>Edit admin pw</Button>
                                                {profile && profile.id !== selectedAdmin.id ? <Button
                                                    sx={[styles.verticalBtn, styles.plainBtn]}
                                                    variant="contained"
                                                    onClick={() => setOpenModal('delete')}>Delete admin</Button> : undefined}
                                            </>)
                                            : (<Button sx={styles.verticalBtn} variant="contained" color="secondary" onClick={() => setOpenModal('reactivate')}>Reactivate</Button>)
                                        }
                                    </Container>
                                </>
                            ) : undefined}
                        </Grid>
                    </BasePanelBox>
                </BasePanel>
                <BaseSheet maxWidth='lg' widerPaper={true}>
                    <Grid size={12}>
                        <Grid container>
                            <Grid sx={{ display: "flex", justifyContent: "flex-start" }} size={6}>
                                <Typography variant="h5" align-left='true'>
                                    Use this page to manage admin users.
                                </Typography>
                            </Grid>
                            <Grid sx={{ display: "flex", justifyContent: "flex-end" }} size={6}>
                                <Typography component="div">
                                    <Grid component="label" container spacing={1} sx={{
                                        alignItems: "center"
                                    }}>
                                        <Grid sx={{ fontWeight: 'bold' }}>Include Active Admins Only: </Grid>
                                        <Grid>Off</Grid>
                                        <Grid>
                                            <Switch
                                                checked={activeOnly}
                                                onChange={handleToggleChange}
                                                name="activeOnly"
                                            />
                                        </Grid>
                                        <Grid>On</Grid>
                                    </Grid>
                                </Typography>
                            </Grid>
                        </Grid>
                        <Container sx={{ ...styles.dfb, padding: 0 }}>
                            <TableContainer component={Paper}>
                                <Table sx={styles.table} aria-label="admin data table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Username</TableCell>
                                            <TableCell align="left">Customer</TableCell>
                                            <TableCell align="left">Role</TableCell>
                                            <TableCell align="left">Email</TableCell>
                                            <TableCell align="left">Active</TableCell>
                                            <TableCell align="right">Last&nbsp;login&nbsp;at</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {adminList().map(admin => (
                                            <TableRow sx={selectedAdmin.id === admin.id ? styles.highlight : undefined} id={admin.id} key={admin.id} onClick={handleTableRowClick}>
                                                <TableCell component="th" scope="row">
                                                    {admin.username}
                                                </TableCell>
                                                <TableCell align="left">{admin.customerName}</TableCell>
                                                <TableCell align="left">{admin.role}</TableCell>
                                                <TableCell align="left">{admin.email}</TableCell>
                                                <TableCell align="left">{admin.active ? "YES" : "NO"}</TableCell>
                                                <TableCell align="right">{admin.lastLoginAt ? admin.lastLoginAt : "No logins yet"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Container>
                    </Grid>
                </BaseSheet>
            </Grid >
            {openModal === 'add' && <AddAdminModal open={true} handleClose={closeAddAdminModal} />}
            {openModal === 'editpw' && <EditAdminPasswordModal open={true} handleClose={closePWModal} profile={selectedAdmin} />}
            {openModal === 'delete' && <DeleteAdminModal open={true} handleClose={closeDeleteModal} profile={selectedAdmin} />}
            {openModal === 'reactivate' && <ReactivateAdminModal open={true} handleClose={closeReactivateAdminModal} profile={selectedAdmin} />}
        </>
    );
}